// ============================================
// MODALCONVERTIRSUCURSAL.JS — "Convertir en sucursal de..." (Sprint Sucursales 1)
// ============================================
// Convierte una o varias EMPRESAS (razones sociales) en SUCURSALES colgadas
// directo de su grupo. Flujo en 3 pasos:
//   1. Destino (el grupo de cada empresa; el backend exige que coincida).
//   2. Tabla previa por empresa: crear sucursal nueva / reusar gemela detectada,
//      conteos de lo que se re-apunta y campos fiscales que se perderian (ambar).
//   3. Confirmacion escribiendo el NUMERO de empresas (patron del borrado permanente).
// Ejecucion en lote: una llamada (= una transaccion) por empresa; si una falla,
// las demas siguen. El 409 de gemela se resuelve por fila con "Usar gemela".
// Solo re-apunta y archiva en el backend — aqui no se borra nada.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  API_BASE_CLI, PP, COLOR_OSCURO, COLOR_FONDO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE, COLOR_PRINCIPAL,
  estiloBotonPrimario, estiloBotonSecundario,
} from './helpersClientes';
import { authHeaders } from '../utils/storage';

const AMBAR = '#b45309';
const AMBAR_BG = '#fef3c7';
const VERDE = '#1D9E75';
const ROJO = '#e05c5c';

export default function ModalConvertirSucursal({ empresas, onCerrar, onConvertidas }) {
  // filas: una por empresa. { empresa, preview|null, errorPreview|null,
  //   estado: 'pendiente'|'convirtiendo'|'exito'|'gemela_pendiente'|'error',
  //   resultado|null, mensaje|null, gemela|null }
  const [filas, setFilas] = useState(() => (empresas || []).map(e => ({
    empresa: e, preview: null, errorPreview: null,
    estado: 'pendiente', resultado: null, mensaje: null, gemela: null,
  })));
  const [cargandoPreview, setCargandoPreview] = useState(true);
  const [confirmacion, setConfirmacion] = useState('');
  const [ejecutando, setEjecutando] = useState(false);
  const [terminado, setTerminado] = useState(false);

  // Tabla previa: pide al backend la previsualizacion exacta de cada empresa
  // (gemela detectada, campos fiscales, conteos) — solo lectura.
  useEffect(() => {
    let activo = true;
    (async () => {
      const previews = await Promise.all((empresas || []).map(async (e) => {
        try {
          const r = await fetch(
            `${API_BASE_CLI}/api/clientes/razones-sociales/${e.id}/preview-conversion`,
            { headers: authHeaders() }
          ).then(x => x.json());
          if (r.ok) return { preview: r, errorPreview: null };
          return { preview: null, errorPreview: r.error || 'No se pudo previsualizar' };
        } catch {
          return { preview: null, errorPreview: 'Sin conexion al previsualizar' };
        }
      }));
      if (!activo) return;
      setFilas(prev => prev.map((f, i) => ({
        ...f,
        preview: previews[i].preview,
        errorPreview: previews[i].errorPreview,
        gemela: previews[i].preview?.gemela_detectada || null,
      })));
      setCargandoPreview(false);
    })();
    return () => { activo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const convertibles = useMemo(
    () => filas.filter(f => f.preview && f.preview.convertible),
    [filas]
  );
  const grupoUnico = useMemo(() => {
    const ids = new Set((empresas || []).map(e => e.grupoId).filter(Boolean));
    return ids.size === 1 ? (empresas.find(e => e.grupoId) || {}) : null;
  }, [empresas]);

  const confirmada = String(convertibles.length) === confirmacion.trim();

  // Convierte UNA fila (una transaccion en el backend). gemelaId opcional
  // (reintento tras 409 o gemela detectada en la previa).
  const convertirFila = useCallback(async (idx, gemelaId) => {
    setFilas(prev => prev.map((f, i) => (i === idx ? { ...f, estado: 'convirtiendo' } : f)));
    const f = filas[idx];
    try {
      const body = {
        destino_grupo_id: f.empresa.grupoId,
        motivo: 'Conversion empresa -> sucursal desde la pantalla Clientes',
      };
      if (gemelaId) body.usar_gemela_id = gemelaId;
      if (f.preview && f.preview.campos_fiscales.length > 0) body.forzar_fiscales = true;
      const r = await fetch(
        `${API_BASE_CLI}/api/clientes/razones-sociales/${f.empresa.id}/convertir-en-sucursal`,
        { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify(body) }
      );
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) {
        setFilas(prev => prev.map((x, i) => (i === idx
          ? { ...x, estado: 'exito', resultado: d, mensaje: null } : x)));
        return;
      }
      if (r.status === 409 && d.gemela_sugerida) {
        setFilas(prev => prev.map((x, i) => (i === idx
          ? { ...x, estado: 'gemela_pendiente', gemela: d.gemela_sugerida, mensaje: d.error } : x)));
        return;
      }
      setFilas(prev => prev.map((x, i) => (i === idx
        ? { ...x, estado: 'error', mensaje: d.error || 'Error al convertir' } : x)));
    } catch {
      setFilas(prev => prev.map((x, i) => (i === idx
        ? { ...x, estado: 'error', mensaje: 'Sin conexion' } : x)));
    }
  }, [filas]);

  // Lote: secuencial, una por una; si una falla, las demas siguen.
  const ejecutarLote = useCallback(async () => {
    if (!confirmada || ejecutando) return;
    setEjecutando(true);
    for (let i = 0; i < filas.length; i++) {
      const f = filas[i];
      if (!f.preview || !f.preview.convertible) continue;
      // eslint-disable-next-line no-await-in-loop
      await convertirFila(i, f.gemela ? f.gemela.id : null);
    }
    setEjecutando(false);
    setTerminado(true);
  }, [confirmada, ejecutando, filas, convertirFila]);

  const hechas = filas.filter(f => f.estado === 'exito').length;
  const pendientesGemela = filas.filter(f => f.estado === 'gemela_pendiente').length;

  // Sin whiteSpace:nowrap (hallazgo de Don Porfirio): el chip de "NO CONVERTIBLE"
  // lleva texto dinamico del backend y debe poder envolver en 360px sin desbordar.
  const chip = (texto, color, bg) => (
    <span style={{
      background: bg, color, padding: '2px 8px', borderRadius: 99,
      fontSize: 10, fontWeight: 700, maxWidth: '100%', wordBreak: 'break-word',
    }}>{texto}</span>
  );

  return (
    <div
      onClick={() => !ejecutando && onCerrar()}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.55)', zIndex: 1300,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: 'min(94vw, 560px)',
          maxHeight: '90vh', overflowY: 'auto', padding: 20,
          fontFamily: PP, color: COLOR_GRIS, boxSizing: 'border-box',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 800, color: COLOR_OSCURO }}>
          Convertir en sucursal de…
        </div>

        {/* Paso 1 — destino */}
        <div style={{ fontSize: 12, color: COLOR_GRIS, margin: '6px 0 14px' }}>
          {grupoUnico
            ? <>Destino: <strong style={{ color: COLOR_OSCURO }}>grupo {grupoUnico.grupoNombre || `#${grupoUnico.grupoId}`}</strong> (las sucursales cuelgan directo del grupo).</>
            : 'Cada razón social se convertira en sucursal de SU propio grupo (las sucursales cuelgan directo del grupo).'}
          {' '}El registro original se archiva, no se borra; todo lo vinculado se re-apunta a la sucursal.
        </div>

        {/* Paso 2 — tabla previa */}
        {cargandoPreview ? (
          <div style={{ color: COLOR_GRIS_CLARO, fontSize: 13, padding: 20, textAlign: 'center' }}>
            Calculando previsualizacion…
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
            {filas.map((f, i) => {
              const p = f.preview;
              return (
                <div key={f.empresa.id} style={{
                  border: `1px solid ${COLOR_BORDE}`, borderRadius: 10, padding: '10px 12px',
                  background: f.estado === 'exito' ? '#e8faf5' : (f.estado === 'error' ? '#fde8e8' : '#fff'),
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, flex: '1 1 160px', minWidth: 0 }}>
                      🏢 {f.empresa.nombre}
                    </span>
                    {f.estado === 'exito' && chip(f.resultado?.creada ? 'CONVERTIDA (sucursal nueva)' : 'CONVERTIDA (gemela reusada)', VERDE, '#e8faf5')}
                    {f.estado === 'convirtiendo' && chip('CONVIRTIENDO…', COLOR_PRINCIPAL, COLOR_FONDO)}
                    {f.estado === 'error' && chip('ERROR', ROJO, '#fde8e8')}
                    {f.estado === 'gemela_pendiente' && chip('DECIDIR GEMELA', AMBAR, AMBAR_BG)}
                    {f.estado === 'pendiente' && p && !p.convertible && chip(`NO CONVERTIBLE: ${p.motivo_no_convertible}`, ROJO, '#fde8e8')}
                    {f.estado === 'pendiente' && f.errorPreview && chip('SIN PREVIA', ROJO, '#fde8e8')}
                  </div>
                  {p && p.convertible && f.estado !== 'exito' && (
                    <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <span>{f.gemela
                        ? <>Reusara la sucursal existente <strong style={{ color: COLOR_OSCURO }}>"{f.gemela.nombre_interno}"</strong></>
                        : 'Se creara una sucursal nueva'}</span>
                      {(p.conteos.roles_contacto + p.conteos.oportunidades + p.conteos.zonas + p.conteos.sucursales_hijas) > 0 ? (
                        <span>Re-apunta: {p.conteos.oportunidades > 0 && `${p.conteos.oportunidades} expediente(s) `}
                          {p.conteos.roles_contacto > 0 && `${p.conteos.roles_contacto} contacto(s) `}
                          {p.conteos.sucursales_hijas > 0 && `${p.conteos.sucursales_hijas} sucursal(es) hija(s) `}
                          {p.conteos.zonas > 0 && `${p.conteos.zonas} zona(s)`}</span>
                      ) : (
                        <span>Nada vinculado que mover</span>
                      )}
                      {p.campos_fiscales.length > 0 && (
                        <span style={{ color: AMBAR, fontWeight: 700 }}>
                          ⚠️ Se perderan datos fiscales: {p.campos_fiscales.join(', ')}
                        </span>
                      )}
                    </div>
                  )}
                  {f.mensaje && f.estado !== 'gemela_pendiente' && (
                    <div style={{ fontSize: 11, color: ROJO, marginTop: 4 }}>{f.mensaje}</div>
                  )}
                  {f.estado === 'gemela_pendiente' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, color: AMBAR }}>
                        Ya existe la sucursal "{f.gemela?.nombre_interno}". ¿Usarla?
                      </span>
                      <button
                        onClick={() => convertirFila(i, f.gemela.id)}
                        style={{ ...estiloBotonSecundario, minHeight: 44, fontSize: 12 }}
                      >Usar gemela y reintentar</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Paso 3 — confirmacion numerica + ejecucion */}
        {!terminado && !cargandoPreview && (
          <div style={{ background: COLOR_FONDO, borderRadius: 10, padding: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: COLOR_GRIS, marginBottom: 8 }}>
              Se convertiran <strong style={{ color: COLOR_OSCURO }}>{convertibles.length}</strong> razon(es) social(es).
              Para confirmar, escribe el numero <strong>{convertibles.length}</strong>:
            </div>
            <input
              type="number"
              inputMode="numeric"
              value={confirmacion}
              onChange={(e) => setConfirmacion(e.target.value)}
              placeholder={`Escribe ${convertibles.length} para confirmar`}
              disabled={ejecutando}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '12px 14px',
                fontSize: 16, fontFamily: PP, border: `1px solid ${COLOR_BORDE}`,
                borderRadius: 10, outline: 'none', color: COLOR_OSCURO,
              }}
            />
          </div>
        )}

        {(ejecutando || terminado) && (
          <div style={{ fontSize: 12, color: COLOR_GRIS, marginBottom: 12 }}>
            Progreso: {hechas} de {convertibles.length} convertidas
            {pendientesGemela > 0 && ` · ${pendientesGemela} esperando decision de gemela`}
            {filas.some(f => f.estado === 'error') && ` · ${filas.filter(f => f.estado === 'error').length} con error`}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={onCerrar}
            disabled={ejecutando}
            style={{ ...estiloBotonSecundario, minHeight: 44 }}
          >{terminado ? 'Cerrar' : 'Cancelar'}</button>
          {!terminado && (
            <button
              onClick={ejecutarLote}
              disabled={!confirmada || ejecutando || cargandoPreview || convertibles.length === 0}
              style={{
                ...estiloBotonPrimario, minHeight: 44,
                opacity: (!confirmada || ejecutando || convertibles.length === 0) ? 0.6 : 1,
                cursor: (!confirmada || ejecutando || convertibles.length === 0) ? 'not-allowed' : 'pointer',
              }}
            >{ejecutando ? 'Convirtiendo…' : `Convertir ${convertibles.length} razon(es) social(es)`}</button>
          )}
          {terminado && hechas > 0 && (
            <button
              onClick={() => onConvertidas && onConvertidas()}
              style={{ ...estiloBotonPrimario, minHeight: 44 }}
            >Cerrar y actualizar lista</button>
          )}
        </div>
      </div>
    </div>
  );
}
